<script>
import navigation from "../components/global/navigation.vue";
import nonPremiumFilesScreen from "../components/files/nonPremiumFilesScreen.vue";
import Multiselect from "vue-multiselect";
import _ from "lodash";

const FALLBACK_THUMB = "/img/icon-attachment.svg";

const DOC_TYPE_OPTIONS = [
  { value: "", label: "Any type" },
  { value: "image", label: "Image" },
  { value: "document", label: "Document" },
  { value: "video", label: "Video" },
  { value: "audio", label: "Audio" },
  { value: "web", label: "Web" },
];

function makeEmptyFilters() {
  return {
    uploaderId: "",
    contacts: [],
    docTypeGroup: "",
    dateFrom: "",
    dateTo: "",
  };
}

const FILTER_LABEL = {
  uploaderId: "Uploader",
  docTypeGroup: "Type",
  dateFrom: "From",
  dateTo: "To",
};

function formatBytes(bytes) {
  if (bytes == null || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n < 10 && i > 0 ? n.toFixed(1) : Math.round(n)} ${units[i]}`;
}

function displayValue(key, value, ctx) {
  if (key === "docTypeGroup") {
    const opt = DOC_TYPE_OPTIONS.find((o) => o.value === value);
    return opt ? opt.label : value;
  }
  if (key === "uploaderId") {
    const u = ctx.uploaders.find((x) => String(x.id) === String(value));
    return u ? u.name : value;
  }
  return value;
}

export default {
  name: "FilesList",
  components: { navigation, nonPremiumFilesScreen, Multiselect },
  data() {
    return {
      isLoading: true,
      isFetching: false,
      isLoadingMore: false,
      files: [],
      totalFiles: 0,
      pageSize: 100,
      uploaders: [],
      approversByVersion: {},
      contactSearchResults: [],
      contactSearchLoading: false,
      search: "",
      filterModalOpen: false,
      draftFilters: makeEmptyFilters(),
      appliedFilters: makeEmptyFilters(),
      docTypeOptions: DOC_TYPE_OPTIONS,
      fallbackThumb: FALLBACK_THUMB,
      openMenuId: null,
      selectedIds: [],
      isDownloading: false,
      isCreatingProof: false,
    };
  },
  computed: {
    uploaderOptions() {
      return [
        { value: "", label: "Any uploader" },
        ...this.uploaders.map((u) => ({ value: String(u.id), label: u.name })),
      ];
    },
    activeFilters() {
      const items = [];
      for (const [key, value] of Object.entries(this.appliedFilters)) {
        if (key === "contacts") {
          for (const c of value) {
            items.push({
              key: `contact:${c.id}`,
              label: "Contact",
              value: c.name,
              removeContactId: c.id,
            });
          }
          continue;
        }
        if (value === "" || value == null) continue;
        items.push({
          key,
          label: FILTER_LABEL[key],
          value: displayValue(key, value, { uploaders: this.uploaders }),
        });
      }
      return items;
    },
    hasActiveFilters() {
      return this.activeFilters.length > 0;
    },
    selectedSet() {
      return new Set(this.selectedIds);
    },
    hasSelection() {
      return this.selectedIds.length > 0;
    },
    allVisibleSelected() {
      if (this.filteredFiles.length === 0) return false;
      return this.filteredFiles.every((f) => this.selectedSet.has(f.id));
    },
    canSelectAllPage() {
      return !this.allVisibleSelected && this.filteredFiles.length > 0;
    },
    bulkCountWord() {
      return this.selectedIds.length === 1 ? "file" : "files";
    },
    draftFilterCount() {
      return Object.values(this.draftFilters).filter((v) => v !== "" && v != null).length;
    },
    filteredFiles() {
      const q = this.search.trim().toLowerCase();
      if (!q) return this.files;
      return this.files.filter((file) => {
        return (
          (file.name || "").toLowerCase().includes(q) ||
          (file.proofName || "").toLowerCase().includes(q) ||
          (file.uploadedByUserName || "").toLowerCase().includes(q)
        );
      });
    },
    hasFilesAccess() {
      const sku = _.get(this, "$A.Auth.account.subscriptionState.sku", "");
      if (!sku) return false;
      if (sku.includes("starter")) return false;
      return sku.includes("standard") || sku.includes("premium") || sku.includes("enterprise");
    },
  },
  methods: {
    formatDate(iso) {
      return this.$A.FormatDateToString(iso);
    },
    formatSize(bytes) {
      return formatBytes(bytes);
    },
    onThumbError(e) {
      e.target.src = FALLBACK_THUMB;
      e.target.classList.add("is-loaded", "is-fallback");
    },
    onThumbLoad(e) {
      e.target.classList.add("is-loaded");
    },
    openFile(file) {
      if (!file.downloadUrl) return;
      window.open(file.downloadUrl, "_blank", "noopener");
    },
    openProof(file) {
      window.open(`/proof-item?id=${file.proofId}`, "_blank", "noopener");
    },
    downloadFile(file) {
      if (!file.downloadUrl) return;
      const a = document.createElement("a");
      a.href = file.downloadUrl;
      a.download = file.name || "";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    },
    toggleMenu(id, event) {
      if (event) event.stopPropagation();
      this.openMenuId = this.openMenuId === id ? null : id;
    },
    closeMenu() {
      this.openMenuId = null;
    },
    handleMenuAction(action, file) {
      this.openMenuId = null;
      if (action === "download") this.downloadFile(file);
      else if (action === "view") this.openFile(file);
      else if (action === "proof") this.openProof(file);
      else if (action === "create-proof") this.createProofFromFiles([file]);
    },
    createProofFromFiles(files) {
      if (this.isCreatingProof || !files || files.length === 0) return;
      this.isCreatingProof = true;
      window.$A.filesForProof = files.map((f) => ({
        name: f.name,
        sizeBytes: f.originalFileBytes,
        mimeType: f.mimeType,
        urlSource: f.downloadUrl,
      }));
      this.$router.push("/create");
    },
    handleDocClick() {
      if (this.openMenuId != null) this.closeMenu();
    },
    isSelected(id) {
      return this.selectedSet.has(id);
    },
    toggleSelect(id, event) {
      if (event) event.stopPropagation();
      const set = new Set(this.selectedIds);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      this.selectedIds = [...set];
    },
    selectAllOnPage() {
      const set = new Set(this.selectedIds);
      this.filteredFiles.forEach((f) => set.add(f.id));
      this.selectedIds = [...set];
    },
    clearSelection() {
      this.selectedIds = [];
    },
    async downloadSelected() {
      if (!this.hasSelection || this.isDownloading) return;
      this.isDownloading = true;
      try {
        await this.$A.ProofFileService.BulkDownloadVersionFiles({ ids: [...this.selectedIds] });
      } catch (e) {
        console.error("Bulk download failed:", e);
        alert("Failed to download files. Please try again.");
      }
      this.isDownloading = false;
    },
    createProofFromSelected() {
      const set = this.selectedSet;
      const picked = this.files.filter((f) => set.has(f.id));
      if (picked.length === 0) return;
      this.createProofFromFiles(picked);
    },
    openFilter() {
      this.draftFilters = {
        ...this.appliedFilters,
        contacts: [...this.appliedFilters.contacts],
      };
      this.filterModalOpen = true;
      this.runContactSearch("");
    },
    closeFilter() {
      this.filterModalOpen = false;
    },
    async applyFilters() {
      this.appliedFilters = { ...this.draftFilters };
      this.filterModalOpen = false;
      await this.fetchFiles();
    },
    clearDraft() {
      this.draftFilters = makeEmptyFilters();
    },
    async clearAllApplied() {
      this.appliedFilters = makeEmptyFilters();
      await this.fetchFiles();
    },
    async removeFilter(chip) {
      if (chip.removeContactId != null) {
        this.appliedFilters = {
          ...this.appliedFilters,
          contacts: this.appliedFilters.contacts.filter((c) => c.id !== chip.removeContactId),
        };
      } else {
        this.appliedFilters = { ...this.appliedFilters, [chip.key]: "" };
      }
      await this.fetchFiles();
    },
    apiFilters() {
      const a = this.appliedFilters;
      return {
        uploaderId: a.uploaderId || undefined,
        contactIds: a.contacts.length ? a.contacts.map((c) => c.id) : undefined,
        docTypeGroup: a.docTypeGroup || undefined,
        from: a.dateFrom || undefined,
        to: a.dateTo || undefined,
      };
    },
    async fetchFiles() {
      this.isFetching = true;
      try {
        const params = { ...this.apiFilters(), limit: this.pageSize, offset: 0 };
        const page = await this.$A.ProofFileService.GetAllVersionFiles(params);
        this.files = page.items || [];
        this.totalFiles = page.total || 0;
        this.fetchApproversForFiles();
      } catch (e) {
        console.error("Failed to load files:", e);
      }
      this.isFetching = false;
    },
    async loadMoreFiles() {
      if (this.isLoadingMore || this.files.length >= this.totalFiles) return;
      this.isLoadingMore = true;
      try {
        const params = { ...this.apiFilters(), limit: this.pageSize, offset: this.files.length };
        const page = await this.$A.ProofFileService.GetAllVersionFiles(params);
        const newItems = page.items || [];
        const seen = new Set(this.files.map((f) => f.id));
        const deduped = newItems.filter((f) => !seen.has(f.id));
        this.files = this.files.concat(deduped);
        this.totalFiles = page.total || this.totalFiles;
        this.fetchApproversForFiles();
      } catch (e) {
        console.error("Failed to load more files:", e);
      }
      this.isLoadingMore = false;
    },
    async fetchUploaders() {
      try {
        this.uploaders = await this.$A.ProofFileService.GetVersionFileUploaders();
      } catch (e) {
        console.error("Failed to load uploaders:", e);
      }
    },
    async runContactSearch(q) {
      this.contactSearchLoading = true;
      try {
        const pinIds = this.draftFilters.contacts.map((c) => c.id);
        this.contactSearchResults = await this.$A.ProofFileService.SearchVersionFileContacts(q, pinIds);
      } catch (e) {
        console.error("Failed to search contacts:", e);
      }
      this.contactSearchLoading = false;
    },
    onContactSearch(q) {
      this._debouncedContactSearch(q || "");
    },
    async fetchApproversForFiles() {
      const versionIds = Array.from(new Set(this.files.map((f) => f.proofVersionId)));
      if (!versionIds.length) {
        this.approversByVersion = {};
        return;
      }
      try {
        const rows = await this.$A.ProofFileService.GetApproversByVersionIds(versionIds);
        const map = {};
        for (const row of rows) {
          if (!map[row.proofVersionId]) map[row.proofVersionId] = [];
          map[row.proofVersionId].push({ id: row.approverId, name: row.name });
        }
        this.approversByVersion = map;
      } catch (e) {
        console.error("Failed to load approvers:", e);
        this.approversByVersion = {};
      }
    },
    approverTooltip(file) {
      const list = this.approversByVersion[file.proofVersionId];
      if (!list || !list.length) return "No approvers on this version";
      return "Sent to: " + list.map((a) => a.name).join(", ");
    },
    initThumbObserver() {
      if (this._thumbObserver) this._thumbObserver.disconnect();
      const root = this.$refs.scrollContainer || null;
      this._thumbObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const img = entry.target;
            const ds = img.getAttribute("data-src");
            if (ds && !img.src) img.src = ds;
            this._thumbObserver.unobserve(img);
          }
        },
        { root, rootMargin: "200px 0px", threshold: 0.01 }
      );
    },
    observeThumbnails() {
      if (!this._thumbObserver) this.initThumbObserver();
      this.$nextTick(() => {
        const imgs = this.$el.querySelectorAll("img.lazy-thumb:not([src])");
        imgs.forEach((img) => this._thumbObserver.observe(img));
      });
    },
    initSentinelObserver() {
      if (this._sentinelObserver) this._sentinelObserver.disconnect();
      const root = this.$refs.scrollContainer || null;
      this._sentinelObserver = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) this.loadMoreFiles();
          }
        },
        { root, rootMargin: "400px 0px", threshold: 0 }
      );
    },
    observeSentinel() {
      if (!this._sentinelObserver) this.initSentinelObserver();
      this.$nextTick(() => {
        const el = this.$refs.loadMoreSentinel;
        if (!el) return;
        this._sentinelObserver.disconnect();
        this.initSentinelObserver();
        this._sentinelObserver.observe(el);
      });
    },
  },
  watch: {
    filteredFiles() {
      this.observeThumbnails();
      this.observeSentinel();
    },
  },
  created() {
    this._debouncedContactSearch = _.debounce((q) => this.runContactSearch(q), 200);
  },
  async mounted() {
    if (!this.hasFilesAccess) {
      this.isLoading = false;
      return;
    }
    this.initThumbObserver();
    this.initSentinelObserver();
    document.addEventListener("click", this.handleDocClick);
    await Promise.all([this.fetchFiles(), this.fetchUploaders()]);
    this.isLoading = false;
    this.observeThumbnails();
    this.observeSentinel();
  },
  beforeDestroy() {
    if (this._thumbObserver) this._thumbObserver.disconnect();
    if (this._sentinelObserver) this._sentinelObserver.disconnect();
    document.removeEventListener("click", this.handleDocClick);
  },
};
</script>

<template>
  <div class="nav-margin">
    <navigation />
    <nonPremiumFilesScreen v-if="!hasFilesAccess" />
    <template v-else>
    <div
      class="action-bar bg-white border-bottom border-gray-300 d-flex gap-12 justify-content-between flex-wrap py-10 px-4 sticky-top"
    >
      <div class="search-wrap">
        <img src="/img/search.svg" width="16" alt="search-icon" class="search-icon" />
        <input
          type="text"
          class="search-input"
          placeholder="Search files, proofs, uploaders"
          v-model="search"
        />
      </div>

      <div class="d-flex align-items-center gap-12">
        <span v-if="!isLoading" class="fs-12 text-gray-500 count-cell">
          <span v-if="isFetching" class="fetching-dot" aria-hidden="true"></span>
          {{ totalFiles.toLocaleString() }} {{ totalFiles === 1 ? "file" : "files" }}
        </span>
        <button class="tertiary-btn filter-btn" @click="openFilter">
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            aria-hidden="true"
          >
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          Filter
          <span v-if="activeFilters.length" class="filter-badge">
            {{ activeFilters.length }}
          </span>
        </button>
      </div>
    </div>

    <transition name="chip-bar">
      <div v-if="hasActiveFilters" class="chip-bar">
        <div class="chip-scroll">
          <span
            v-for="f in activeFilters"
            :key="f.key"
            class="active-chip"
          >
            <span class="chip-label">{{ f.label }}:</span>
            <span class="chip-value">{{ f.value }}</span>
            <button
              type="button"
              class="chip-close"
              :aria-label="`Remove ${f.label} filter`"
              @click="removeFilter(f)"
            >
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round">
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="6" y1="18" x2="18" y2="6" />
              </svg>
            </button>
          </span>
        </div>
        <button class="chip-clear-all" @click="clearAllApplied">Clear all</button>
      </div>
    </transition>

    <div
      ref="scrollContainer"
      class="content-view"
      :class="{ 'content-view-with-chips': hasActiveFilters }"
    >
      <div v-if="isLoading" class="px-4 py-4 text-gray-500">Loading files...</div>

      <ul v-else-if="filteredFiles.length" class="file-list list-unstyled mb-0">
        <li
          v-for="file in filteredFiles"
          :key="file.id"
          class="file-row border-bottom border-opacity-10 d-flex gap-2 justify-content-between align-items-center px-4 py-14 bg-white"
          :class="{ 'is-selected': isSelected(file.id) }"
          @click="openFile(file)"
        >
          <div class="flex-fill d-flex align-items-center gap-3 min-w-0">
            <label class="row-check-wrap" @click.stop>
              <input
                type="checkbox"
                class="row-checkbox"
                :checked="isSelected(file.id)"
                @change="toggleSelect(file.id, $event)"
              />
            </label>
            <div
              class="thumb flex-shrink-0"
              :class="{ 'thumb-fallback': !file.thumbnailUrl }"
            >
              <img
                v-if="file.thumbnailUrl"
                :data-src="file.thumbnailUrl"
                @error="onThumbError"
                @load="onThumbLoad"
                decoding="async"
                alt=""
                class="thumb-img lazy-thumb"
              />
              <img
                v-else
                :src="fallbackThumb"
                @load="onThumbLoad"
                decoding="async"
                alt=""
                class="thumb-img is-fallback"
              />
            </div>
            <div class="min-w-0">
              <div class="fs-14 fw-normal text-gray-900 mb-0 text-truncate">
                {{ file.name }}
              </div>
              <a
                v-if="file.proofName"
                class="proof-link text-truncate"
                :href="`/proof-item?id=${file.proofId}`"
                v-b-tooltip.hover.top="{
                  title: approverTooltip(file),
                  delay: { show: 150, hide: 80 },
                  boundary: 'window',
                  customClass: 'files-tooltip',
                }"
                target="_blank"
                rel="noopener"
                @click.stop
              >
                {{ file.proofName }}
              </a>
            </div>
          </div>

          <div class="d-flex align-items-center gap-12 flex-shrink-0 row-meta">
            <span v-if="file.docTypeGroup" class="meta-chip">
              {{ file.docTypeGroup }}
            </span>
            <span v-if="file.failed" class="status-pill status-failed">Failed</span>
            <span v-else-if="!file.conversionJobCompletedAt" class="status-pill status-processing">
              Processing
            </span>
            <span class="fs-12 text-gray-500 size-cell">
              {{ formatSize(file.originalFileBytes) }}
            </span>
            <span class="fs-12 text-gray-500 uploader-cell text-truncate">
              {{ file.uploadedByUserName || "—" }}
            </span>
            <span class="fs-12 text-gray-500 date-cell">
              {{ formatDate(file.createdAt) }}
            </span>

            <div class="row-menu" @click.stop>
              <button
                type="button"
                class="row-menu-trigger"
                :class="{ 'is-open': openMenuId === file.id }"
                aria-haspopup="true"
                :aria-expanded="openMenuId === file.id ? 'true' : 'false'"
                aria-label="File actions"
                @click="toggleMenu(file.id, $event)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <circle cx="12" cy="5" r="1.75" />
                  <circle cx="12" cy="12" r="1.75" />
                  <circle cx="12" cy="19" r="1.75" />
                </svg>
              </button>

              <div
                v-if="openMenuId === file.id"
                class="row-menu-popover"
                role="menu"
              >
                  <button
                    type="button"
                    class="row-menu-item"
                    role="menuitem"
                    @click="handleMenuAction('download', file)"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                    Download
                  </button>
                  <button
                    type="button"
                    class="row-menu-item"
                    role="menuitem"
                    @click="handleMenuAction('view', file)"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M14 3h7v7" />
                      <path d="M10 14L21 3" />
                      <path d="M21 14v5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5" />
                    </svg>
                    View File
                  </button>
                  <button
                    type="button"
                    class="row-menu-item"
                    role="menuitem"
                    @click="handleMenuAction('proof', file)"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <polyline points="14 2 14 8 20 8" />
                      <line x1="8" y1="13" x2="16" y2="13" />
                      <line x1="8" y1="17" x2="13" y2="17" />
                    </svg>
                    View Proof
                  </button>
                  <button
                    type="button"
                    class="row-menu-item"
                    role="menuitem"
                    :disabled="isCreatingProof"
                    @click="handleMenuAction('create-proof', file)"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                      <path d="M12 5v14" />
                      <path d="M5 12h14" />
                    </svg>
                    Create new proof
                  </button>
              </div>
            </div>
          </div>
        </li>
      </ul>

      <div v-else class="empty-wrap">
        <div class="empty-card">
          <div class="empty-headline">
            <h2>No files match</h2>
            <p v-if="hasActiveFilters || search">Try clearing a filter or adjusting your search.</p>
            <p v-else>Files uploaded to proofs will show up here.</p>
          </div>
        </div>
      </div>

      <div
        v-if="!isLoading && files.length && files.length < totalFiles"
        ref="loadMoreSentinel"
        class="load-more-sentinel"
      >
        <span v-if="isLoadingMore" class="load-more-spinner" aria-hidden="true"></span>
        <span class="load-more-text">
          {{ isLoadingMore
              ? "Loading more…"
              : `${files.length.toLocaleString()} of ${totalFiles.toLocaleString()} loaded` }}
        </span>
      </div>
    </div>

    <transition name="filter-modal">
      <div
        v-if="filterModalOpen"
        class="filter-modal-backdrop"
        @click.self="closeFilter"
      >
        <div
          class="filter-modal"
          role="dialog"
          aria-labelledby="filter-modal-title"
        >
          <div class="filter-modal-header">
            <div>
              <h3 id="filter-modal-title" class="filter-modal-title">Filter files</h3>
              <p class="filter-modal-sub">Narrow the list by proof, uploader, type, date, or status.</p>
            </div>
            <button class="filter-close" @click="closeFilter" aria-label="Close">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div class="filter-modal-body">
            <div class="filter-field">
              <label class="filter-label" for="filter-uploader">Uploader</label>
              <div class="select-wrap">
                <select
                  id="filter-uploader"
                  class="filter-input filter-select"
                  v-model="draftFilters.uploaderId"
                >
                  <option v-for="opt in uploaderOptions" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
                <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="1 1 5 5 9 1" />
                </svg>
              </div>
            </div>

            <div class="filter-field">
              <label class="filter-label" for="filter-contact">Contact</label>
              <Multiselect
                id="filter-contact"
                v-model="draftFilters.contacts"
                :options="contactSearchResults"
                :multiple="true"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                :internal-search="false"
                :loading="contactSearchLoading"
                :show-no-results="true"
                :options-limit="50"
                placeholder="Search contacts by name or email"
                track-by="id"
                label="name"
                @search-change="onContactSearch"
              >
                <template #noResult>
                  <span class="ms-empty">No contacts match.</span>
                </template>
                <template #noOptions>
                  <span class="ms-empty">Start typing to search.</span>
                </template>
              </Multiselect>
            </div>

            <div class="filter-field">
              <label class="filter-label" for="filter-type">Type</label>
              <div class="select-wrap">
                <select
                  id="filter-type"
                  class="filter-input filter-select"
                  v-model="draftFilters.docTypeGroup"
                >
                  <option v-for="opt in docTypeOptions" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </option>
                </select>
                <svg class="select-caret" width="10" height="6" viewBox="0 0 10 6" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">
                  <polyline points="1 1 5 5 9 1" />
                </svg>
              </div>
            </div>

            <div class="filter-row two-col">
              <div class="filter-field">
                <label class="filter-label" for="filter-from">Uploaded from</label>
                <input
                  id="filter-from"
                  type="date"
                  class="filter-input"
                  v-model="draftFilters.dateFrom"
                />
              </div>
              <div class="filter-field">
                <label class="filter-label" for="filter-to">Uploaded to</label>
                <input
                  id="filter-to"
                  type="date"
                  class="filter-input"
                  v-model="draftFilters.dateTo"
                />
              </div>
            </div>
          </div>

          <div class="filter-modal-footer">
            <button class="ghost-btn" :disabled="draftFilterCount === 0" @click="clearDraft">
              Clear all
            </button>
            <div class="footer-right">
              <button class="text-btn" @click="closeFilter">Cancel</button>
              <button class="primary-btn" @click="applyFilters">
                Apply
                <span v-if="draftFilterCount" class="apply-count">{{ draftFilterCount }}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>

    </template>
    <transition name="bulk-bar">
      <div v-if="hasSelection" class="bulk-bar" role="region" aria-label="Bulk actions">
        <div class="bulk-bar-inner">
          <div class="bulk-count">
            <strong class="bulk-count-num">{{ selectedIds.length }}</strong>
            <span class="bulk-count-label">{{ bulkCountWord }} selected</span>
          </div>

          <button
            v-if="canSelectAllPage"
            type="button"
            class="bulk-promote-btn"
            @click="selectAllOnPage"
          >
            Select all
          </button>

          <div class="bulk-actions">
            <button class="text-btn bulk-clear" @click="clearSelection" :disabled="isDownloading || isCreatingProof">Clear</button>
            <button
              class="ghost-btn bulk-create-btn"
              @click="createProofFromSelected"
              :disabled="isDownloading || isCreatingProof"
            >
              <svg v-if="!isCreatingProof" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
              <span v-else class="bulk-spinner bulk-spinner-dark" aria-hidden="true"></span>
              {{ isCreatingProof ? "Preparing files…" : "Create new proof" }}
            </button>
            <button
              class="primary-btn bulk-download-btn"
              @click="downloadSelected"
              :disabled="isDownloading || isCreatingProof"
            >
              <svg v-if="!isDownloading" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              <span v-else class="bulk-spinner" aria-hidden="true"></span>
              {{ isDownloading ? "Preparing zip…" : "Download (.zip)" }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<style>
/* Unscoped: bootstrap-vue tooltips render at document.body */
.tooltip.files-tooltip {
  opacity: 1 !important;
}
.tooltip.files-tooltip .tooltip-inner {
  background: #0f172a;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.35;
  padding: 6px 10px;
  border-radius: 6px;
  max-width: 280px;
  box-shadow: 0 6px 20px rgba(15, 23, 42, 0.22), 0 1px 2px rgba(15, 23, 42, 0.12);
  letter-spacing: 0.005em;
}
.tooltip.files-tooltip.bs-tooltip-top .arrow::before,
.tooltip.files-tooltip.bs-tooltip-auto[x-placement^="top"] .arrow::before {
  border-top-color: #0f172a;
}
.tooltip.files-tooltip.bs-tooltip-bottom .arrow::before,
.tooltip.files-tooltip.bs-tooltip-auto[x-placement^="bottom"] .arrow::before {
  border-bottom-color: #0f172a;
}
.tooltip.files-tooltip.bs-tooltip-left .arrow::before,
.tooltip.files-tooltip.bs-tooltip-auto[x-placement^="left"] .arrow::before {
  border-left-color: #0f172a;
}
.tooltip.files-tooltip.bs-tooltip-right .arrow::before,
.tooltip.files-tooltip.bs-tooltip-auto[x-placement^="right"] .arrow::before {
  border-right-color: #0f172a;
}
</style>

<style scoped>
.search-wrap {
  position: relative;
  width: 320px;
  max-width: 100%;
}
.search-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0.55;
  pointer-events: none;
}
.search-input {
  width: 100%;
  height: 36px;
  padding: 0 12px 0 36px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #1f2937;
  background: #fff;
  outline: none;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.search-input:focus {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.search-input::placeholder {
  color: #9ca3af;
}
.count-cell {
  min-height: 36px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.row-check-wrap {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 4px;
  cursor: pointer;
  flex-shrink: 0;
}
.row-checkbox {
  appearance: none;
  -webkit-appearance: none;
  width: 16px;
  height: 16px;
  border: 1.5px solid #d1d5db;
  border-radius: 4px;
  background: #fff;
  cursor: pointer;
  position: relative;
  transition: background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
  margin: 0;
  flex-shrink: 0;
}
.row-checkbox:hover {
  border-color: #019256;
}
.row-checkbox:checked {
  background: #019256;
  border-color: #019256;
}
.row-checkbox:checked::after {
  content: "";
  position: absolute;
  left: 4px;
  top: 1px;
  width: 5px;
  height: 9px;
  border: solid #fff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}
.row-checkbox:indeterminate {
  background: #019256;
  border-color: #019256;
}
.row-checkbox:indeterminate::after {
  content: "";
  position: absolute;
  left: 3px;
  top: 6px;
  width: 8px;
  height: 2px;
  background: #fff;
  border-radius: 1px;
}
.row-checkbox:focus-visible {
  outline: 2px solid #019256;
  outline-offset: 2px;
}
.fetching-dot {
  width: 6px;
  height: 6px;
  border-radius: 999px;
  background: #019256;
  animation: fetching-pulse 900ms ease-in-out infinite;
}
@keyframes fetching-pulse {
  0%, 100% { opacity: 0.35; transform: scale(0.85); }
  50%      { opacity: 1;    transform: scale(1.15); }
}

.tertiary-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 12px;
  background: transparent;
  border: 1px solid transparent;
  color: #4b5563;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border-radius: 6px;
  transition: background-color 120ms ease, color 120ms ease, border-color 120ms ease;
}
.tertiary-btn:hover {
  background: #f3f4f6;
  color: #111827;
}
.filter-btn {
  position: relative;
}
.filter-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: #019256;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.01em;
  box-shadow: 0 0 0 2px #fff;
}

/* Chip bar */
.chip-bar {
  position: sticky;
  top: 56px;
  z-index: 5;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px 10px 16px;
  background: #fbfbfc;
  border-bottom: 1px solid #eceef1;
  min-height: 48px;
}
.chip-scroll {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}
.active-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 28px;
  padding: 0 6px 0 10px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e1e4ea;
  font-size: 12px;
  color: #1f2937;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.03);
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.active-chip:hover {
  border-color: rgba(1, 146, 86, 0.45);
  box-shadow: 0 1px 2px rgba(1, 146, 86, 0.08);
}
.chip-label {
  color: #6b7280;
  font-weight: 500;
}
.chip-value {
  color: #111827;
  font-weight: 600;
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.chip-close {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin-left: 2px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 999px;
  color: #9ca3af;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease, transform 120ms ease;
}
.chip-close:hover {
  background: rgba(1, 146, 86, 0.12);
  color: #019256;
  transform: scale(1.06);
}
.chip-close:focus-visible {
  outline: 2px solid #019256;
  outline-offset: 1px;
}
.chip-clear-all {
  flex-shrink: 0;
  background: transparent;
  border: 0;
  color: #6b7280;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  transition: color 120ms ease, background-color 120ms ease;
}
.chip-clear-all:hover {
  color: #019256;
  background: rgba(1, 146, 86, 0.08);
}

.chip-bar-enter-active,
.chip-bar-leave-active {
  transition: opacity 140ms ease, transform 160ms ease;
}
.chip-bar-enter-from,
.chip-bar-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
.chip-bar-enter,
.chip-bar-leave-to {
  /* Vue 2 fallback names */
  opacity: 0;
  transform: translateY(-4px);
}

.content-view {
  height: calc(100vh - 130px);
  overflow-y: auto;
}
.content-view-with-chips {
  height: calc(100vh - 178px);
}

.file-list {
  background: #fff;
}

.file-row {
  cursor: pointer;
  position: relative;
  transition: background-color 120ms ease;
}
.file-row.is-selected {
  background: rgba(1, 146, 86, 0.06);
}
.file-row.is-selected::before {
  opacity: 1;
}
.file-row > .flex-fill {
  min-width: 0;
}
.file-row .min-w-0 {
  min-width: 0;
  flex: 1 1 0;
}
.file-row::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: #019256;
  opacity: 0;
  transition: opacity 120ms ease;
}
.file-row:hover {
  background: #f3f4f6;
}
.file-row:hover::before {
  opacity: 1;
}

.thumb {
  width: 56px;
  height: 56px;
  border-radius: 8px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  position: relative;
}
.thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0;
  transition: opacity 180ms ease;
}
.thumb-img.is-loaded {
  opacity: 1;
}
.thumb-fallback .thumb-img,
.thumb-img.is-fallback {
  width: 60%;
  height: 60%;
  object-fit: contain;
  opacity: 0.55;
}
.thumb-fallback .thumb-img.is-loaded,
.thumb-img.is-fallback.is-loaded {
  opacity: 0.55;
}

.row-meta {
  font-size: 13px;
}

.meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f3f4f6;
  color: #4b5563;
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
}

.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  background: #ebebed;
  color: #444;
}
.status-failed {
  background: #fee2e2;
  color: #b91c1c;
}
.status-processing {
  background: #fef3c7;
  color: #92400e;
}

.proof-link {
  display: inline-block;
  max-width: 100%;
  font-size: 12px;
  color: #6b7280;
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: color 120ms ease, border-color 120ms ease;
}
.proof-link:hover {
  color: #019256;
  border-bottom-color: rgba(1, 146, 86, 0.45);
}
.proof-link:focus-visible {
  outline: 2px solid #019256;
  outline-offset: 2px;
  border-radius: 2px;
}

.size-cell {
  min-width: 60px;
  text-align: right;
}
.uploader-cell {
  min-width: 120px;
  max-width: 160px;
  text-align: right;
}
.date-cell {
  min-width: 96px;
  text-align: right;
}

.row-menu {
  position: relative;
  display: flex;
  align-items: center;
}
.row-menu-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #6b7280;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.row-menu-trigger:hover,
.row-menu-trigger.is-open {
  background: #ebebed;
  color: #111827;
}
.row-menu-trigger:focus-visible {
  outline: 2px solid #019256;
  outline-offset: 1px;
}
.row-menu-popover {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 20;
  min-width: 168px;
  padding: 4px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14), 0 2px 6px rgba(15, 23, 42, 0.06);
  display: flex;
  flex-direction: column;
}
.row-menu-item {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: transparent;
  border: 0;
  border-radius: 6px;
  color: #1f2937;
  font-size: 13px;
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.row-menu-item svg {
  color: #6b7280;
  flex-shrink: 0;
  transition: color 120ms ease;
}
.row-menu-item:hover {
  background: rgba(1, 146, 86, 0.08);
  color: #019256;
}
.row-menu-item:hover svg {
  color: #019256;
}
.row-menu-item:focus-visible {
  outline: 2px solid #019256;
  outline-offset: -2px;
}

.bulk-bar {
  position: fixed;
  left: 50%;
  bottom: 24px;
  transform: translateX(-50%);
  z-index: 1050;
  max-width: 92vw;
}
.bulk-bar-inner {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 10px 12px 10px 20px;
  background: #0f172a;
  color: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.35), 0 4px 10px rgba(15, 23, 42, 0.15);
}
.bulk-count {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  font-size: 13px;
}
.bulk-count-num {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.005em;
}
.bulk-count-label {
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}
.bulk-promote-btn {
  background: transparent;
  border: 0;
  color: rgba(255, 255, 255, 0.7);
  font-size: 13px;
  font-weight: 500;
  padding: 6px 4px;
  cursor: pointer;
  position: relative;
  transition: color 120ms ease;
}
.bulk-promote-btn::after {
  content: "";
  position: absolute;
  left: 4px;
  right: 4px;
  bottom: 2px;
  height: 1px;
  background: currentColor;
  opacity: 0;
  transition: opacity 120ms ease;
}
.bulk-promote-btn:hover {
  color: #fff;
}
.bulk-promote-btn:hover::after {
  opacity: 0.6;
}
.bulk-promote-btn:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.4);
  outline-offset: 2px;
  border-radius: 3px;
}

.bulk-actions {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}
.bulk-actions .text-btn {
  color: rgba(255, 255, 255, 0.7);
}
.bulk-actions .text-btn:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}
.bulk-actions .text-btn:disabled {
  color: rgba(255, 255, 255, 0.35);
  cursor: not-allowed;
}
.bulk-download-btn {
  padding: 9px 16px;
}
.bulk-download-btn:disabled {
  opacity: 0.75;
  cursor: progress;
}
.bulk-create-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 14px;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease;
}
.bulk-create-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.14);
  border-color: rgba(255, 255, 255, 0.32);
}
.bulk-create-btn:disabled {
  opacity: 0.55;
  cursor: progress;
}
.bulk-spinner-dark {
  border-color: rgba(255, 255, 255, 0.25);
  border-top-color: #fff;
}

.row-menu-item:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.bulk-spinner {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.35);
  border-top-color: #fff;
  border-radius: 999px;
  animation: bulk-spin 700ms linear infinite;
}
@keyframes bulk-spin {
  to { transform: rotate(360deg); }
}

.bulk-bar-enter-active,
.bulk-bar-leave-active {
  transition: opacity 160ms ease, transform 200ms cubic-bezier(0.16, 1, 0.3, 1);
}
.bulk-bar-enter-from,
.bulk-bar-enter,
.bulk-bar-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(16px);
}

.row-menu-popover {
  animation: row-menu-pop 140ms cubic-bezier(0.16, 1, 0.3, 1);
  transform-origin: top right;
}
@keyframes row-menu-pop {
  from { opacity: 0; transform: translateY(-4px) scale(0.97); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
}

.load-more-sentinel {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 20px 16px 28px;
  font-size: 12px;
  color: #6b7280;
}
.load-more-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(1, 146, 86, 0.25);
  border-top-color: #019256;
  border-radius: 999px;
  animation: load-more-spin 700ms linear infinite;
}
@keyframes load-more-spin {
  to { transform: rotate(360deg); }
}

.empty-wrap {
  padding: 56px 24px;
  display: flex;
  justify-content: center;
}
.empty-card {
  width: 100%;
  max-width: 480px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 40px;
  text-align: center;
}
.empty-headline h2 {
  font-family: 'Open Sans', system-ui, -apple-system, sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 8px;
}
.empty-headline p {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* Filter modal */
.filter-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 80px;
  z-index: 1080;
}
.filter-modal {
  background: #fff;
  width: 100%;
  max-width: 560px;
  border-radius: 12px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.28);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.filter-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 20px 24px 14px;
  border-bottom: 1px solid #f1f3f5;
}
.filter-modal-title {
  font-family: 'Open Sans', system-ui, -apple-system, sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 4px;
  letter-spacing: -0.005em;
}
.filter-modal-sub {
  font-size: 13px;
  color: #6b7280;
  margin: 0;
}
.filter-close {
  background: transparent;
  border: 0;
  color: #6b7280;
  padding: 4px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color 120ms ease, color 120ms ease;
}
.filter-close:hover {
  background: #f3f4f6;
  color: #111827;
}
.filter-modal-body {
  padding: 18px 24px 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-height: 60vh;
  overflow-y: auto;
}
.filter-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.filter-row.two-col {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}
.filter-label {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  letter-spacing: 0.01em;
  text-transform: uppercase;
}
.filter-input {
  width: 100%;
  height: 38px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 14px;
  color: #1f2937;
  background: #fff;
  outline: none;
  transition: border-color 120ms ease, box-shadow 120ms ease;
  font-family: inherit;
}
.filter-input:focus {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.filter-input::placeholder {
  color: #9ca3af;
}
.select-wrap {
  position: relative;
}
.filter-select {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  padding-right: 32px;
  cursor: pointer;
}
.select-caret {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.filter-field >>> .multiselect {
  min-height: 38px;
  font-size: 14px;
}
.filter-field >>> .multiselect__tags {
  min-height: 38px;
  padding: 6px 36px 0 8px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}
.filter-field >>> .multiselect--active .multiselect__tags {
  border-color: #019256;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
.filter-field >>> .multiselect__tag {
  background: #019256;
  color: #fff;
  font-size: 12px;
  font-weight: 500;
  border-radius: 6px;
  padding: 3px 24px 3px 8px;
  margin-right: 6px;
  margin-bottom: 4px;
}
.filter-field >>> .multiselect__tag-icon {
  border-radius: 6px;
  line-height: 22px;
}
.filter-field >>> .multiselect__tag-icon:after {
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
}
.filter-field >>> .multiselect__tag-icon:hover {
  background: rgba(0, 0, 0, 0.18);
}
.filter-field >>> .multiselect__option--highlight {
  background: rgba(1, 146, 86, 0.12);
  color: #111827;
}
.filter-field >>> .multiselect__option--highlight:after {
  background: rgba(1, 146, 86, 0.12);
  color: #019256;
}
.filter-field >>> .multiselect__option--selected.multiselect__option--highlight {
  background: #019256;
  color: #fff;
}
.filter-field >>> .multiselect__input,
.filter-field >>> .multiselect__single {
  font-size: 14px;
  margin: 0;
  padding: 2px 0;
  line-height: 1.4;
}
.filter-field >>> .multiselect__placeholder {
  font-size: 14px;
  color: #9ca3af;
  margin: 0;
  padding: 2px 0;
}
.filter-field .ms-empty {
  color: #6b7280;
  font-size: 13px;
}

.filter-modal-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 24px 18px;
  border-top: 1px solid #f1f3f5;
  background: #fbfbfc;
}
.footer-right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.primary-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #019256;
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(1, 146, 86, 0.2);
  transition: background-color 120ms ease, transform 80ms ease, box-shadow 120ms ease;
}
.primary-btn:hover {
  background: #017a48;
  box-shadow: 0 2px 6px rgba(1, 146, 86, 0.25);
}
.primary-btn:active {
  transform: translateY(1px);
  box-shadow: 0 1px 1px rgba(1, 146, 86, 0.2);
}
.apply-count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.2);
  font-size: 11px;
  font-weight: 700;
}
.ghost-btn {
  background: #fff;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease, border-color 120ms ease, color 120ms ease;
}
.ghost-btn:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #9ca3af;
}
.ghost-btn:disabled {
  color: #9ca3af;
  cursor: not-allowed;
  opacity: 0.6;
}
.text-btn {
  background: transparent;
  border: 0;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: color 120ms ease, background-color 120ms ease;
}
.text-btn:hover {
  color: #111827;
  background: #f3f4f6;
}

.filter-modal-enter-active,
.filter-modal-leave-active {
  transition: opacity 160ms ease;
}
.filter-modal-enter-active .filter-modal,
.filter-modal-leave-active .filter-modal {
  transition: transform 200ms cubic-bezier(0.16, 1, 0.3, 1), opacity 160ms ease;
}
.filter-modal-enter-from,
.filter-modal-enter,
.filter-modal-leave-to {
  opacity: 0;
}
.filter-modal-enter-from .filter-modal,
.filter-modal-enter .filter-modal,
.filter-modal-leave-to .filter-modal {
  transform: translateY(8px) scale(0.985);
  opacity: 0;
}

@media (max-width: 640px) {
  .action-bar {
    padding: 12px 16px !important;
    gap: 8px !important;
  }
  .search-wrap {
    width: 100%;
  }
  .file-row {
    padding: 12px 16px !important;
  }
  .row-meta {
    gap: 8px !important;
  }
  .uploader-cell,
  .date-cell {
    display: none;
  }
  .filter-modal-backdrop {
    padding: 16px;
    padding-top: 24px;
  }
  .filter-row.two-col {
    grid-template-columns: 1fr;
  }
  .chip-bar {
    top: 0;
  }
}
@media (max-width: 400px) {
  .row-meta .meta-chip {
    display: none;
  }
}
</style>
